'use client';

import { Fragment } from 'react';

export type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPrefetch?: (page: number) => void;
  itemCount?: number;
  totalItems?: number;
  itemName?: string;
};

/**
 * Generic table pagination component with prefetch capability
 */
export const TablePagination = ({
  currentPage,
  totalPages,
  onPageChange,
  onPrefetch,
  itemCount = 0,
  totalItems = 0,
  itemName = 'items',
}: PaginationProps) => {
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const MAX_VISIBLE_PAGES = 5;

    if (totalPages <= MAX_VISIBLE_PAGES) {
      // If we have fewer pages than the max, show all of them
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always include first page
      pages.push(1);

      // Calculate start and end page numbers
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're near the start
      if (currentPage <= 2) {
        endPage = 4;
      }

      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        startPage = totalPages - 3;
      }

      // Add ellipsis if needed
      if (startPage > 2) {
        pages.push('...');
      }

      // Add middle page numbers
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis if needed
      if (endPage < totalPages - 1) {
        pages.push('...');
      }

      // Always include last page
      pages.push(totalPages);
    }

    return pages;
  };

  const handlePrefetch = (page: number) => {
    if (onPrefetch) {
      onPrefetch(page);
    }
  };

  const pageNumbers = getPageNumbers();

  if (totalPages <= 1) {
    return (
      <div className="w-full flex flex-col items-center gap-2">
        {itemCount > 0 && (
          <div className="text-sm text-gray-500">
            Showing
            {' '}
            {itemCount}
            {' '}
            of
            {' '}
            {totalItems}
            {' '}
            {itemName}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center gap-2">
      <div className="flex justify-center space-x-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          onMouseEnter={() => currentPage > 1 && handlePrefetch(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded-md bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors hover:bg-gray-200"
          type="button"
          aria-label="Previous page"
        >
          Prev
        </button>

        {pageNumbers.map((page, index) => (
          <Fragment key={index}>
            {page === '...' ? (
              <span className="px-3 py-1">...</span>
            ) : (
              <button
                onClick={() => onPageChange(page as number)}
                onMouseEnter={() => handlePrefetch(page as number)}
                className={`px-3 py-1 rounded-md ${currentPage === page ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                type="button"
                aria-label={`Page ${page}`}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </button>
            )}
          </Fragment>
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          onMouseEnter={() => currentPage < totalPages && handlePrefetch(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded-md bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors hover:bg-gray-200"
          type="button"
          aria-label="Next page"
        >
          Next
        </button>
      </div>

      {itemCount > 0 && (
        <div className="text-sm text-gray-500">
          Showing
          {' '}
          {itemCount}
          {' '}
          of
          {' '}
          {totalItems}
          {' '}
          {itemName}
        </div>
      )}
    </div>
  );
};

/**
 * Loading skeleton for the pagination component
 */
export const PaginationSkeleton = () => {
  return (
    <div className="w-full flex flex-col items-center gap-2">
      <div className="flex justify-center space-x-2">
        <div className="px-3 py-1 rounded-md bg-gray-200 text-transparent animate-pulse">Prev</div>
        {[1, 2, 3].map(i => (
          <div key={i} className="px-3 py-1 rounded-md bg-gray-200 text-transparent animate-pulse w-8">
            {i}
          </div>
        ))}
        <div className="px-3 py-1 rounded-md bg-gray-200 text-transparent animate-pulse">Next</div>
      </div>
      <div className="text-sm text-gray-200 animate-pulse w-36 h-4 rounded-md"></div>
    </div>
  );
};
